import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ClipboardList, AlertTriangle, Receipt, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import StatusPill from "@/components/StatusPill";

const KPI = ({ icon: Icon, label, value, tone = "navy", tid }) => {
  const tones = {
    navy: "bg-[#181854] text-white",
    ice: "bg-[#ECEDF6] text-[#181854]",
    warn: "bg-[#FFF7ED] text-[#9A3412]",
    success: "bg-[#DCFCE7] text-[#166534]",
    danger: "bg-[#FEE2E2] text-[#991B1B]",
  };
  return (
    <div
      data-testid={tid}
      className="bg-white rounded-xl border border-[#E4E6EF] p-5 flex items-start gap-4 shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
    >
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tones[tone]}`}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#6A6E85]">
          {label}
        </p>
        <p className="text-3xl font-bold text-[#14162E] mt-1">{value}</p>
      </div>
    </div>
  );
};

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-[#6A6E85]">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[#181854] tracking-tight">Dashboard</h1>
        <p className="text-[#6A6E85] mt-1">
          What&apos;s happening at GT Refrigeration &amp; Aircon today.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI icon={Calendar} label="Today's jobs" value={data.kpis.today_jobs} tone="navy" tid="kpi-today" />
        <KPI icon={ClipboardList} label="Unassigned" value={data.kpis.unassigned} tone="ice" tid="kpi-unassigned" />
        <KPI icon={AlertTriangle} label="Overdue services" value={data.kpis.overdue_services} tone="warn" tid="kpi-overdue" />
        <KPI icon={Receipt} label="Awaiting invoice" value={data.kpis.awaiting_invoice} tone="success" tid="kpi-invoice" />
        <KPI icon={TrendingUp} label="Active this week" value={data.kpis.active_this_week} tone="ice" tid="kpi-week" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#181854]">Today&apos;s schedule</h2>
            <Link to="/office/schedule" className="text-sm text-[#4F9CE0] hover:underline">
              Open schedule board →
            </Link>
          </div>
          {data.today_schedule.length === 0 ? (
            <p className="text-[#6A6E85] text-sm py-6 text-center">
              Nothing scheduled for today.
            </p>
          ) : (
            <ul className="divide-y divide-[#E4E6EF]">
              {data.today_schedule.map((j) => (
                <li key={j.id}>
                  <Link
                    to={`/office/jobs/${j.id}`}
                    className={`status-border-${j.status} flex items-center justify-between gap-3 py-3 pl-3 pr-2 hover:bg-[#F5F6FA] rounded-lg transition-colors duration-200`}
                    data-testid={`today-job-${j.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-[#14162E] truncate">{j.title}</p>
                      <p className="text-xs text-[#6A6E85] truncate">
                        {j.client_name} · {j.site_address}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-[#6A6E85]">{fmtTime(j.scheduled_at)}</span>
                      <StatusPill status={j.status} />
                      <span className="text-xs text-[#6A6E85] hidden sm:inline">
                        {j.tech_name || "—"}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl border border-[#E4E6EF] p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-[#E8912A]" />
            <h2 className="text-lg font-semibold text-[#181854]">Needs attention</h2>
          </div>
          {data.needing_attention.length === 0 ? (
            <p className="text-[#6A6E85] text-sm py-6 text-center">All clear.</p>
          ) : (
            <ul className="space-y-2">
              {data.needing_attention.map((j) => (
                <li key={j.id}>
                  <Link
                    to={`/office/jobs/${j.id}`}
                    className={`status-border-${j.status} block p-3 rounded-lg bg-[#F5F6FA] hover:bg-[#ECEDF6] transition-colors duration-200`}
                  >
                    <p className="font-medium text-sm text-[#14162E] truncate">{j.title}</p>
                    <p className="text-xs text-[#6A6E85] truncate">{j.client_name}</p>
                    <div className="mt-1.5">
                      <StatusPill status={j.status} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
